/** Public site identity, used by the FAQ page's canonical URL, the JSON-LD, robots and sitemap.
 *
 *  ⚠️ NEXT_PUBLIC_* IS BAKED AT BUILD TIME (see CLAUDE.md). A runtime env var never reaches this
 *  value in the browser bundle, so an image built without NEXT_PUBLIC_SITE_URL ships the default
 *  below — which is why the default is the real production origin and not a localhost placeholder.
 *  A canonical URL pointing at localhost would tell every crawler the page does not exist. */
export const SITE_URL = (
  process.env.NEXT_PUBLIC_SITE_URL ?? "https://twoke.io"
).replace(/\/$/, "");

export const SITE_NAME = "twoke";

export const CONTACT_EMAIL = "enfo@twoke.io";

/**
 * WHATSAPP — digits only, full international format, NO "+" and no spaces (that is what wa.me
 * requires; a formatted number silently 404s on their side).
 *
 * EMPTY MEANS THE LINK IS NOT RENDERED, deliberately. A "message us on WhatsApp" button that
 * opens a chat with a number nobody owns is worse than no button — the visitor concludes support
 * is dead rather than that a field was blank. Same rule for every social entry below.
 */
export const WHATSAPP_NUMBER = "18498754383"; // WhatsApp Business, +1 849 875 4383 (DO)

/** Prefilled first message, so an incoming chat arrives with context instead of "hi". */
export const WHATSAPP_MESSAGE =
  "Hi twoke — I have a question about copy trading.";

export const whatsappUrl = (): string | null =>
  WHATSAPP_NUMBER
    ? `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`
    : null;

/**
 * Social profiles. `url: ""` hides the entry — see the WhatsApp note above for why unset must
 * render nothing rather than a guess.
 *
 * These are also emitted as `sameAs` on the Organization JSON-LD in the footer, which is how a
 * search or answer engine ties the accounts to the brand as one entity rather than three unrelated
 * things called "twoke". That makes a wrong URL here worse than a dead link: it would assert that
 * someone else's account IS twoke.
 */
export type Social = { name: string; url: string; handle: string };

export const SOCIALS: Social[] = [
  { name: "X", url: "https://x.com/twokeio", handle: "@twokeio" },
  // UPDATED 2026-08-22 on the owner's word ("facebook page is twokeio", URL pasted verbatim).
  // The previous handle was twokeAVEM (owner-confirmed 2026-08-15). A server-side fetch on
  // 2026-08-22 still resolved /twokeavem to a page named "Twokeavem" and /twokeio to
  // Facebook's generic shell — Facebook walls bots, so that is not proof either way; the
  // owner opened the page himself. If /twokeio ever shows "not available" in a browser, the
  // username change did not take on Facebook's side — fix it there, not here.
  { name: "Facebook", url: "https://www.facebook.com/twokeio", handle: "twokeio" },
];

export const activeSocials = (): Social[] => SOCIALS.filter((s) => s.url);

/** Content review date, shown on the page and emitted as `dateModified`. Bump it when the answers
 *  change — not on every deploy. Answer engines treat freshness as a ranking signal, so an
 *  automatic build timestamp would be a claim about the CONTENT that the build cannot make. */
export const CONTENT_UPDATED = "2026-10-28";
